import { CheckCircle2, Circle, XCircle, HelpCircle, Loader2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AgentStatus } from '@/api/types';

interface StatusBadgeProps {
  status: AgentStatus;
  className?: string;
}

const STATUS_CONFIG: Record<
  AgentStatus,
  { Icon: LucideIcon; color: string; label: string }
> = {
  active: {
    Icon: CheckCircle2,
    color: 'bg-success/10 text-success border-success/30',
    label: 'Active',
  },
  inactive: {
    Icon: Circle,
    color: 'bg-muted text-muted-foreground border-border',
    label: 'Inactive',
  },
  failed: {
    Icon: XCircle,
    color: 'bg-destructive/10 text-destructive border-destructive/30',
    label: 'Failed',
  },
  starting: {
    Icon: Loader2,
    color: 'bg-primary/10 text-primary border-primary/30',
    label: 'Starting',
  },
  activating: {
    Icon: Loader2,
    color: 'bg-primary/10 text-primary border-primary/30',
    label: 'Starting',
  },
  deactivating: {
    Icon: Loader2,
    color: 'bg-muted text-muted-foreground border-border',
    label: 'Stopping',
  },
  unknown: {
    Icon: HelpCircle,
    color: 'bg-muted text-muted-foreground border-border',
    label: 'Unknown',
  },
};

/**
 * StatusBadge — icon + text + color, never color alone (§8.9). Use this
 * for agent status everywhere (cards, tables, detail headers).
 */
export function StatusBadge({ status, className }: StatusBadgeProps) {
  const { Icon, color, label } = STATUS_CONFIG[status] ?? STATUS_CONFIG.unknown;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium',
        color,
        className,
      )}
    >
      <Icon className="h-3 w-3" aria-hidden />
      <span>{label}</span>
    </span>
  );
}
